import { Stack } from '@mui/material'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Typography } from 'ComponentLibrary/src/piazza'
import { DownloadSimpleIcon, FolderIcon, PencilSimpleIcon, PlusCircleIcon } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButton'
import { useEffect, useRef, useState } from 'react'
import { TaskAttachmentItem } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskAttachments/TaskAttachmentItem'
import { isEmpty } from 'lodash'
import { FileUploadModal } from 'ComponentLibrary/src/molecules/FileUploadModal'
import { type FileUploadModalCopy } from 'ComponentLibrary/src/molecules/FileUploadModal/FileUploadModal'
import { type DocumentTagOption } from 'ComponentLibrary/src/molecules/SelectTags/constants'
import { Document, DocumentWithIndex } from 'ComponentLibrary/src/molecules/FileUploadModal/types'
import { FileDropArea } from 'ComponentLibrary/index'
import { type TaskAttachmentItemCopy } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskAttachments/TaskAttachmentItem'

const SECTION_VERTICAL_PADDING = tokens.SizingSpacing200
const HEADER_HEIGHT = tokens.SizingSpacing500
export const TASK_ATTACHMENT_HEADER_TOTAL_HEIGHT = `(2 * ${SECTION_VERTICAL_PADDING} + ${HEADER_HEIGHT})`

type TaskAttachmentsProps = {
    documents: DocumentWithIndex[]
    failedDocumentUploads: string[]
    downloadDocument: (index: number) => void
    removeDocument: (index: number) => void
    updateDocuments: (documents: Document[], onSuccess: () => void) => void
    onDownloadAll: () => void
    onUploadFiles: (files: File[]) => void
    isUploadingDocuments: boolean
    isUpdatingDocuments: boolean
    isDownloadingDocument?: boolean
    isUploadDisabled?: boolean
    uploadDisabledTooltip?: string
    copy?: Partial<TaskAttachmentsCopy>
    fileUploadModalCopy?: Partial<FileUploadModalCopy>
    fileUploadModalTagOptions?: DocumentTagOption[]
}

export type TaskAttachmentsCopy = {
    title: string
    downloadAllAriaLabel: string
    editDocumentsAriaLabel: string
    uploadDocumentsAriaLabel: string
    item: TaskAttachmentItemCopy
}

const DEFAULT_TASK_ATTACHMENTS_COPY: TaskAttachmentsCopy = {
    title: 'Dokumente',
    downloadAllAriaLabel: 'Download all Documents',
    editDocumentsAriaLabel: 'Edit Documents',
    uploadDocumentsAriaLabel: 'Upload Documents',
    item: {
        edit: 'Bearbeiten',
        delete: 'Löschen',
        optionsAriaLabel: 'Aufgaben Anhänge Optionen',
    },
}

export function TaskAttachments({
    documents,
    downloadDocument,
    removeDocument,
    updateDocuments,
    onDownloadAll,
    onUploadFiles,
    isUploadingDocuments,
    isUpdatingDocuments,
    failedDocumentUploads,
    isDownloadingDocument,
    isUploadDisabled = false,
    uploadDisabledTooltip,
    copy,
    fileUploadModalCopy,
    fileUploadModalTagOptions,
}: TaskAttachmentsProps) {
    const resolvedCopy = { ...DEFAULT_TASK_ATTACHMENTS_COPY, ...copy }
    const itemCopy = { ...DEFAULT_TASK_ATTACHMENTS_COPY.item, ...copy?.item }
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
    const [isEditingFiles, setIsEditingFiles] = useState(false)
    const [isOver, setIsOver] = useState(false)

    const fileDropAreaRef = useRef<HTMLDivElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (scrollContainer === null) {
            return
        }

        const handleScroll = () => {
            const scrollTop = scrollContainer.scrollTop
            setIsScrolled(scrollTop > 0)
        }

        scrollContainer.addEventListener('scroll', handleScroll)

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const downloadDocumentById = (documentId: string) => downloadDocument(documents.findIndex((doc) => doc.uuid === documentId))

    const closeFileUploadModal = () => {
        setIsFileUploadModalOpen(false)
        setIsEditingFiles(false)
    }

    const openFileUploadInEditMode = () => {
        setIsEditingFiles(true)
        setIsFileUploadModalOpen(true)
    }

    return (
        <Stack height="100%" width="100%" alignItems="flex-start" position="relative" ref={fileDropAreaRef}>
            {!isUploadDisabled && (
                <FileDropArea
                    isOver={isOver}
                    setIsOver={setIsOver}
                    dragAreaRef={fileDropAreaRef}
                    onDataDrop={(_, files) => {
                        setIsFileUploadModalOpen(true)
                        files && onUploadFiles(files)
                    }}
                />
            )}
            <FileUploadModal
                isOpen={isFileUploadModalOpen}
                close={closeFileUploadModal}
                isEditing={isEditingFiles}
                isUploading={isUploadingDocuments}
                isPersisting={isUpdatingDocuments}
                uploadDocuments={onUploadFiles}
                downloadDocument={downloadDocumentById}
                deleteDocument={removeDocument}
                documents={documents}
                updateDocuments={updateDocuments}
                failedDocumentUploads={failedDocumentUploads}
                isDownloadingDocument={isDownloadingDocument}
                copy={fileUploadModalCopy}
                tagOptions={fileUploadModalTagOptions}
            />
            <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
                padding={`${SECTION_VERTICAL_PADDING} ${tokens.SizingSpacing300}`}
                boxSizing="border-box"
                sx={isScrolled ? { ...tokens.Popup } : {}}
            >
                <Stack direction="row" height={HEADER_HEIGHT} gap={tokens.SizingSpacing100} alignItems="center">
                    <FolderIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                    <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                        {resolvedCopy.title}
                    </Typography>
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {documents.length}
                    </Typography>
                </Stack>
                <Stack direction="row" height={tokens.SizingSpacing500} gap={tokens.SizingSpacing150} alignItems="center">
                    <IconButton
                        disabled={isEmpty(documents)}
                        size="small"
                        variant="secondary"
                        icon={<DownloadSimpleIcon weight="bold" />}
                        ariaLabel={resolvedCopy.downloadAllAriaLabel}
                        onClick={onDownloadAll}
                    />
                    <IconButton
                        disabled={isEmpty(documents)}
                        size="small"
                        variant="secondary"
                        icon={<PencilSimpleIcon weight="bold" />}
                        ariaLabel={resolvedCopy.editDocumentsAriaLabel}
                        onClick={openFileUploadInEditMode}
                    />
                    <IconButton
                        disabled={isUploadDisabled}
                        size="small"
                        variant="secondary"
                        icon={<PlusCircleIcon weight="bold" />}
                        ariaLabel={resolvedCopy.uploadDocumentsAriaLabel}
                        onClick={() => setIsFileUploadModalOpen(true)}
                        tooltip={isUploadDisabled ? uploadDisabledTooltip : undefined}
                    />
                </Stack>
            </Stack>
            <Stack
                width="100%"
                direction="column"
                height="100%"
                boxSizing="border-box"
                padding={`0 ${tokens.SizingSpacing300}`}
                ref={scrollContainerRef}
                sx={{
                    '& > :not(:last-child)': {
                        borderBottom: `1px solid ${tokens.ColorBorderDefault}`,
                    },
                    overflowY: 'auto',
                }}
            >
                {documents.map((taskDocument: Document, index: number) => {
                    return (
                        <TaskAttachmentItem
                            key={taskDocument.url}
                            displayedItem={taskDocument}
                            onClick={() => downloadDocument(index)}
                            onDelete={() => removeDocument(index)}
                            copy={itemCopy}
                        />
                    )
                })}
            </Stack>
        </Stack>
    )
}
